// trendLineOverlay.js
// All chart integration for the trend line indicator.
// ChartNew.jsx imports and calls these functions.
// No React, no store — pure chart operations.
//
// ChartNew.jsx touch points (search "TREND LINE" in ChartNew.jsx):
//   1. import this file
//   2. initTrendLineSeries(chart)        — in INIT CHART useEffect
//   3. drawTrendLine(ref, cache, show)   — after loadChartData + after loadMoreLeft/Right
//   4. applyTrendLineVisibility(ref, show) — in EMA/indicator toggle useEffect
// ============================================================


import { computeTrendLine } from './trendLine';
import { createSeriesMarkers } from 'lightweight-charts';


// ── Style config — change here to adjust appearance ──
const TREND_LINE_STYLE = {
  color:            '#FF6B35',   // orange — distinct from EMAs
  lineWidth:        2,
  priceLineVisible: false,
  lastValueVisible: false,
  crosshairMarkerVisible: false,
};

// ============================================================
// INIT — call once inside INIT CHART useEffect
// Returns the series ref to store in ChartNew
// ============================================================
export function initTrendLineSeries(chart, LineSeries) {
  const series = chart.addSeries(LineSeries, {
    ...TREND_LINE_STYLE,
    visible: false,
  });
  return series;
}

// Store markers instance outside — one per series
let markersInstance = null;

// ============================================================
// DRAW — call after any candle data load or update
// trendLineSeriesRef : React ref holding the series
// ohlcvCache         : ohlcvCacheRef.current (array of {time,high,low,...})
// show               : boolean — is trend line currently visible
// ============================================================
export function drawTrendLine(trendLineSeriesRef, ohlcvCache, show, n = 3,
                              showMarkers = false) {
  if (!trendLineSeriesRef.current) return;
  if (!show) return;                          // don't compute if hidden
  if (!ohlcvCache || ohlcvCache.length < 2) return;

  const { linePoints, swingPoints } = computeTrendLine(ohlcvCache, n);

  if (linePoints.length === 0) return;

  trendLineSeriesRef.current.setData(linePoints);

  // ── R4b: markers OFF by default — line alone stays clean ──
  if (!showMarkers) {
    if (markersInstance) {
      markersInstance.detach();
      markersInstance = null;
    }
    return;
  }

  // ── Mark swing points on chart ──
  const markers = swingPoints
    .map(sp => ({
      time:     sp.time,
      position: sp.type === 'PIVOT_HIGH' ? 'aboveBar' : 'belowBar',
      shape:    'circle',
      color:    sp.type === 'PIVOT_LOW' ? '#26a69a' : '#ef5350',
      size:     1,
      text:     sp.type === 'PIVOT_LOW' ? 'L' : 'H',
    })).sort((a, b) => a.time - b.time);

  // Remove previous markers instance if exists
  if (markersInstance) {
    markersInstance.detach();
    markersInstance = null;
  }
  if (markers.length > 0) {
    markersInstance = createSeriesMarkers(trendLineSeriesRef.current, markers);
  }
}

// ============================================================
// TOGGLE VISIBILITY — call when user toggles the indicator
// show : new boolean value (true = show, false = hide)
// ============================================================
export function applyTrendLineVisibility(trendLineSeriesRef, ohlcvCache, show, n = 3,
                                         showMarkers = false) {
  if (!trendLineSeriesRef.current) return;
  if (show) {
    drawTrendLine(trendLineSeriesRef, ohlcvCache, true, n, showMarkers);
    trendLineSeriesRef.current.applyOptions({ visible: true });
  } else {
    trendLineSeriesRef.current.applyOptions({ visible: false });
  }
}